import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  MessageCircle,
  ArrowRight,
  Mail,
  Phone,
  Clock,
  HelpCircle,
  ChevronRight,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

const PRIORITIES = ["low", "medium", "high", "critical"];
const CATEGORIES = ["withdrawal", "deposit", "kyc", "login", "trading", "other"];

const QUICK_HELP = [
  {
    title: "Deposits & withdrawals",
    desc: "INR and crypto funding, whitelisting, and settlement timelines.",
    category: "deposit",
  },
  {
    title: "KYC verification",
    desc: "Document upload, tier limits, and approval status.",
    category: "kyc",
  },
  {
    title: "Trading & orders",
    desc: "Spot execution, order history, and market access.",
    category: "trading",
  },
];

const STATUS_STYLES = {
  open: "bg-blue-50 text-cm-blue",
  in_progress: "bg-amber-50 text-amber-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-cm-surface text-cm-grey",
};

export default function SupportPage() {
  const [tickets, setTickets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/support/tickets");
      setTickets(data);
      setErr("");
    } catch (e) {
      setErr(getErrorMessage(e));
    }
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, []);

  async function send() {
    if (!reply.trim()) return;
    await api.post(`/api/support/tickets/${open.id}/reply`, { body: reply });
    setReply("");
    const { data } = await api.get("/api/support/tickets");
    setTickets(data);
    setOpen(data.find((t) => t.id === open.id));
  }

  return (
    <AppShell maxWidth="960px">
      <div
        className="rounded-[20px] overflow-hidden mb-8 border border-[var(--cm-border)]"
        style={{
          background:
            "linear-gradient(135deg, #001cbf 0%, #022dee 55%, #0029ff 100%)",
        }}
        data-testid="support-hero"
      >
        <div className="px-6 py-8 lg:px-10 lg:py-10 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 mb-2">
                Help centre
              </p>
              <h1 className="text-[28px] lg:text-[32px] font-extrabold tracking-tight">
                Support
              </h1>
              <p className="mt-2 text-[14px] text-white/80 max-w-[480px] leading-relaxed">
                Raise a ticket and our team will respond — most issues are handled within a few
                hours.
              </p>
            </div>
            <button
              onClick={() => setCreating(true)}
              data-testid="new-ticket"
              className="inline-flex items-center justify-center gap-2 h-[48px] px-6 rounded-[12px] bg-white text-cm-blue font-bold text-[14px] hover:shadow-lg transition shrink-0"
            >
              <Plus size={16} /> New ticket
            </button>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <a
          href="mailto:helpdesk@coinmbx.com"
          className="flex items-start gap-3 p-4 rounded-[14px] bg-white border border-[var(--cm-border)] hover:border-cm-blue/30 transition"
          data-testid="support-email"
        >
          <div className="w-10 h-10 rounded-[10px] bg-cm-lavender grid place-items-center shrink-0">
            <Mail size={18} className="text-cm-blue" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-cm-navy">Email</div>
            <div className="text-[12px] text-cm-grey mt-0.5">helpdesk@coinmbx.com</div>
          </div>
        </a>
        <a
          href="tel:+918796101200"
          className="flex items-start gap-3 p-4 rounded-[14px] bg-white border border-[var(--cm-border)] hover:border-cm-blue/30 transition"
          data-testid="support-phone"
        >
          <div className="w-10 h-10 rounded-[10px] bg-cm-lavender grid place-items-center shrink-0">
            <Phone size={18} className="text-cm-blue" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-cm-navy">Phone</div>
            <div className="text-[12px] text-cm-grey mt-0.5">+91 87961 01200</div>
          </div>
        </a>
        <div className="flex items-start gap-3 p-4 rounded-[14px] bg-white border border-[var(--cm-border)]">
          <div className="w-10 h-10 rounded-[10px] bg-cm-lavender grid place-items-center shrink-0">
            <Clock size={18} className="text-cm-blue" />
          </div>
          <div>
            <div className="text-[13px] font-bold text-cm-navy">Availability</div>
            <div className="text-[12px] text-cm-grey mt-0.5">24×7 helpdesk</div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-[15px] font-bold text-cm-navy mb-3">Common topics</h2>
        <div className="grid sm:grid-cols-3 gap-3">
          {QUICK_HELP.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={() => {
                setCreating(true);
              }}
              className="text-left p-4 rounded-[14px] bg-white border border-[var(--cm-border)] hover:border-cm-blue/25 hover:bg-cm-surface/50 transition group"
              data-testid={`help-topic-${item.category}`}
            >
              <div className="flex items-center justify-between gap-2">
                <HelpCircle size={16} className="text-cm-blue shrink-0" />
                <ChevronRight
                  size={14}
                  className="text-cm-grey opacity-0 group-hover:opacity-100 transition"
                />
              </div>
              <div className="mt-2 text-[13px] font-bold text-cm-navy">{item.title}</div>
              <p className="mt-1 text-[12px] text-cm-grey leading-relaxed">{item.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="mb-4 px-4 py-3 rounded-[10px] bg-red-50 text-[13px] text-red-700 border border-red-100">
          {err}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[15px] font-bold text-cm-navy">Your tickets</h2>
        <Link to="/about" className="text-[12px] font-semibold text-cm-blue hover:underline">
          About CoinMBX
        </Link>
      </div>

      {loading ? (
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-10 text-center text-cm-grey text-[13px]">
          Loading tickets…
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-cm-lavender mx-auto grid place-items-center mb-4">
            <MessageCircle className="text-cm-blue" size={20} />
          </div>
          <h3 className="text-[16px] font-bold text-cm-navy">No tickets yet</h3>
          <p className="mt-1 text-[13px] text-cm-grey max-w-[360px] mx-auto">
            When you raise an issue it will appear here. You can also email{" "}
            <a href="mailto:helpdesk@coinmbx.com" className="text-cm-blue font-semibold hover:underline">
              helpdesk@coinmbx.com
            </a>{" "}
            directly.
          </p>
          <button
            onClick={() => setCreating(true)}
            className="mt-5 inline-flex items-center gap-2 h-[42px] px-5 rounded-[10px] bg-cm-blue text-white font-semibold text-[13px] hover:bg-cm-blue-deep"
          >
            <Plus size={14} /> Raise your first ticket
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-[16px] border border-[var(--cm-border)] overflow-hidden shadow-sm">
          <table className="w-full text-[13px]" data-testid="my-tickets">
            <thead className="bg-cm-surface text-cm-grey text-left text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Subject</th>
                <th className="px-5 py-3 hidden sm:table-cell">Priority</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 hidden md:table-cell">Created</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--cm-border)]">
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-cm-surface/60" data-testid={`my-ticket-${t.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-cm-navy">{t.subject}</td>
                  <td className="px-5 py-3.5 text-cm-grey capitalize hidden sm:table-cell">
                    {t.priority}
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold capitalize ${
                        STATUS_STYLES[t.status] || STATUS_STYLES.open
                      }`}
                    >
                      {t.status.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-cm-grey text-[11px] hidden md:table-cell">
                    {new Date(t.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <button
                      onClick={() => setOpen(t)}
                      className="text-cm-blue text-[12px] font-bold hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateTicketModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      )}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-cm-navy/60 grid place-items-center p-4"
          onClick={() => setOpen(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[16px] w-full max-w-[640px] max-h-[90vh] flex flex-col shadow-xl"
          >
            <div className="p-5 border-b border-[var(--cm-border)] flex justify-between items-start gap-4">
              <div>
                <h3 className="text-[18px] font-bold text-cm-navy">{open.subject}</h3>
                <div className="text-[11px] text-cm-grey mt-1">
                  Priority: {open.priority} · Status: {open.status.replace("_", " ")}
                </div>
              </div>
              <button
                onClick={() => setOpen(null)}
                className="text-cm-grey hover:text-cm-navy text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {open.messages?.map((m, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-[10px] ${
                    m.from === "admin" ? "bg-cm-lavender ml-8 lg:ml-12" : "bg-cm-surface mr-8 lg:mr-12"
                  }`}
                >
                  <div className="text-[10px] uppercase font-bold text-cm-grey">
                    {m.from} · {new Date(m.at).toLocaleString()}
                  </div>
                  <div className="text-[13px] mt-1 whitespace-pre-wrap text-cm-navy">{m.body}</div>
                </div>
              ))}
            </div>
            {open.status !== "closed" && open.status !== "resolved" && (
              <div className="p-4 border-t border-[var(--cm-border)] flex flex-col gap-2">
                <textarea
                  rows={2}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a reply…"
                  className="w-full p-3 border border-[var(--cm-border)] rounded-[10px] text-[13px] focus:border-cm-blue focus:ring-2 focus:ring-cm-blue/10 outline-none"
                  data-testid="user-reply"
                />
                <button
                  onClick={send}
                  className="self-start h-[40px] px-5 rounded-[10px] bg-cm-blue text-white font-semibold flex items-center gap-2 hover:bg-cm-blue-deep"
                >
                  <ArrowRight size={14} /> Reply
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppShell>
  );
}

function CreateTicketModal({ onClose, onCreated }) {
  const [f, setF] = useState({ subject: "", body: "", priority: "medium", category: "other" });
  const [err, setErr] = useState("");
  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/api/support/tickets", f);
      onCreated();
    } catch (e2) {
      setErr(getErrorMessage(e2));
    }
  }
  return (
    <div
      className="fixed inset-0 z-[100] bg-cm-navy/60 grid place-items-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[16px] w-full max-w-[480px] p-6 space-y-4 shadow-xl"
        data-testid="create-ticket-modal"
      >
        <div>
          <h3 className="text-[18px] font-bold text-cm-navy">Raise a ticket</h3>
          <p className="text-[12px] text-cm-grey mt-1">
            Describe your issue and we&rsquo;ll get back to you as soon as possible.
          </p>
        </div>
        <input
          required
          value={f.subject}
          onChange={(e) => setF({ ...f, subject: e.target.value })}
          placeholder="Subject"
          className="w-full h-[42px] px-3 border border-[var(--cm-border)] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
          data-testid="ticket-subject"
        />
        <div className="grid grid-cols-2 gap-3">
          <select
            value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })}
            className="w-full h-[42px] px-3 border border-[var(--cm-border)] rounded-[10px] text-[14px] capitalize focus:border-cm-blue outline-none"
          >
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value })}
            className="w-full h-[42px] px-3 border border-[var(--cm-border)] rounded-[10px] text-[14px] capitalize focus:border-cm-blue outline-none"
            data-testid="ticket-priority"
          >
            {PRIORITIES.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </div>
        <textarea
          required
          rows={5}
          value={f.body}
          onChange={(e) => setF({ ...f, body: e.target.value })}
          placeholder="Describe your issue…"
          className="w-full p-3 border border-[var(--cm-border)] rounded-[10px] text-[14px] focus:border-cm-blue outline-none"
          data-testid="ticket-body"
        />
        {err && <div className="text-[13px] text-red-600">{err}</div>}
        <button
          type="submit"
          className="w-full h-[44px] rounded-[10px] bg-cm-blue text-white font-bold hover:bg-cm-blue-deep"
          data-testid="ticket-submit"
        >
          Submit ticket
        </button>
      </form>
    </div>
  );
}
